/**
 * The side-effecting layer: pushes a consent decision into the actual tags.
 *
 * Two vendors, two very different mechanisms — this is the whole reason the
 * feature isn't a one-liner:
 *
 * **GA4 / gtag.js — Google Consent Mode v2.** gtag.js loads normally and is
 * told, *before* it loads, that the opt-in signals are denied. It then
 * withholds cookies and identifiers on its own. On accept we send
 * `gtag('consent', 'update', ...)` and it starts behaving normally. This is
 * the vendor-sanctioned shape and is what Google's own consent checks look
 * for. Note this is DEFENCE IN DEPTH, not the only gate: `gaPixel.canFire()`
 * additionally refuses to emit any event at all while consent is withheld, so
 * GA4 receives nothing from our code pre-accept.
 *
 * **Meta Pixel — don't load it.** `fbq` has no consent API; there is no
 * "denied" mode. The only way to genuinely not fire is to never inject
 * `fbevents.js` and never call `fbq('init', ...)`. So the bootstrap in
 * `public/index.html` defers the loader behind `window.__theaConsent
 * .loadMetaPixel()` and we invoke it here, once, on accept. Until then
 * `window.fbq` is undefined and every wrapper in `metaPixel.ts` no-ops.
 *
 * Server-side Meta CAPI is untouched by any of this, deliberately — see
 * `metaPixel.ts`.
 *
 * ## Known residual: withdrawal after an accept
 *
 * If a visitor accepts and later declines (via the privacy page's "Change your
 * cookie choices"), we stop calling `fbq` and flip Consent Mode back to
 * denied — but `fbevents.js` is already in the page and cannot be unloaded,
 * and the `_fbp` / `_fbc` cookies it set remain on the domain.
 *
 * Clearing those cookies is NOT done here, on purpose: `metaCapi.ts` reads
 * them on every server-side event, so deleting them would change CAPI
 * behaviour — and CAPI is explicitly out of scope for this change. Whether a
 * withdrawal should also drop the Meta cookies (and accept the CAPI
 * attribution loss that follows) is a product and legal decision, not an
 * engineering one. Flagged for review; see the follow-ups in the handoff.
 */
import { signalsFor, advertisingAllowed, type ConsentStatus } from './consentPolicy';

/**
 * The handshake object the inline bootstrap in `public/index.html` publishes
 * before the tags load. The app adopts it rather than recomputing, so the
 * pre-tag decision and the React state can never disagree.
 */
/**
 * What the bootstrap's geo round trip settles to.
 *
 * `null` means "no answer" — the fetch failed, the response was unusable, or
 * the browser has no `fetch`. It is deliberately NOT a fail-safe outcome: the
 * bootstrap starts the request but never decides what a miss means, because
 * deciding that is `consentGeo.ts`'s job and it has the fallback path.
 */
export interface BootstrapGeoOutcome {
  consentRequired: boolean;
  resolved: boolean;
}

export interface ConsentBootstrap {
  version: string;
  status: ConsentStatus;
  /** Injects fbevents.js and calls fbq('init'). Idempotent. */
  loadMetaPixel?: () => void;
  /**
   * The geo lookup, already in flight (THE-524).
   *
   * The bootstrap issues it from `<head>` so it races the bundle DOWNLOAD
   * instead of queueing behind React boot, the dynamic Firebase import and an
   * anonymous sign-in — the three things that used to eat 86% of the client's
   * deadline and left visitors outside CA/FL looking at a banner the server
   * had already cleared them from.
   *
   * Absent (`undefined` / `null`) when the bootstrap did not start one: an
   * explicit decision is already on file, the browser has no `fetch`, or
   * there is no bootstrap at all (jsdom, a prerender host, a cached
   * index.html from before this shipped). `consentGeo.ts` falls back to
   * calling the callable itself in every one of those cases.
   */
  geo?: Promise<BootstrapGeoOutcome | null> | null;
}

declare global {
  interface Window {
    __theaConsent?: ConsentBootstrap;
  }
}

/** Read the bootstrap handshake, if `public/index.html` ran. */
export function readBootstrap(): ConsentBootstrap | null {
  if (typeof window === 'undefined') return null;
  return window.__theaConsent ?? null;
}

/**
 * Push `status` into both vendors.
 *
 * Safe to call repeatedly: `gtag('consent','update')` is idempotent, and the
 * bootstrap's `loadMetaPixel` guards against double-injection.
 */
export function applyConsentToTags(status: ConsentStatus): void {
  if (typeof window === 'undefined') return;

  // GA4 — Consent Mode v2 update. Uses the raw dataLayer-backed `gtag` shim
  // that index.html defines, NOT gaPixel's `emit`: consent commands are not
  // analytics events and must go through even while events are suppressed.
  try {
    window.gtag?.('consent', 'update', signalsFor(status));
  } catch {
    // A blocked/absent gtag must never break the accept click.
  }

  // Meta Pixel — load on first grant, never before.
  if (advertisingAllowed(status)) {
    try {
      readBootstrap()?.loadMetaPixel?.();
    } catch {
      // Same: an ad-blocked pixel loader must not break the UI.
    }
  }

  // Keep the handshake object in sync so anything reading it late (e.g. a
  // component that mounts after the decision) sees the current status.
  const bootstrap = readBootstrap();
  if (bootstrap) bootstrap.status = status;
}
